import BayFullPageLoader from '../../BayFullPageLoader';
import BayRcDialog from '../../BayRcDialog';
import '../styles/actAsModal.scss';
import type { TranslationFunction } from '../../types/common';

interface ActAsSessionLoaderProps {
	label: string;
	name: string;
	t?: TranslationFunction;
}

// Two lines, not one flowing sentence. The name used to be inlined with `{' '}`
// and a trailing `...`, so it wrapped wherever it happened to land -- "Blad
// Content" on one line and "Testing Content..." on the next, splitting a
// person's name across a line break.
//
// Label and name are separate blocks now, so a break can only fall BETWEEN
// them. `actAsLoadingName` balances a long name across its lines instead of
// orphaning one word, and the ellipsis is part of the same string so it cannot
// wrap onto a line of its own.
export const ActAsSessionLoader = ({ label, name, t }: ActAsSessionLoaderProps) => (
	<BayFullPageLoader
		show
		spinnerSize={48}
		t={t}
		overlayStyle={{
			background: 'transparent',
			backdropFilter: 'none',
			WebkitBackdropFilter: 'none',
		}}
		loadingTextClassName="fs-5 mt-5 text-700"
		loadingText={(
			<>
				<span className="d-block">{label}</span>
				<strong className="d-block actAsLoadingName">{`${name}...`}</strong>
			</>
		)}
	/>
);

interface ActAsSessionOverlayProps {
	show: boolean;
	label: string;
	name: string;
	platform?: string;
	isMobileView?: boolean;
	t?: TranslationFunction;
}

// The same overlay ActAsModal shows while it mints a session, standalone so the
// "stop acting as" buttons can show it too -- they leave the page immediately
// afterwards and used to give no feedback at all between click and reload.
//
// The dialog carries no chrome (actAsModalSessionPending strips its background
// and shadow) and no title, footer or close button. It renders only because it
// owns the blurred backdrop and the focus trap; the loader inside is
// `position: fixed` and centres itself.
//
// onHideFunc is a noop on purpose: the session change is already in flight and
// there is nothing to cancel, so Escape and a backdrop click must not dismiss it.
const ActAsSessionOverlay = ({
	show,
	label,
	name,
	platform,
	isMobileView = false,
	t,
}: ActAsSessionOverlayProps) => (
	<BayRcDialog
		className="actAsModalMobileDialog actAsModalSessionPending"
		closeButton={false}
		mask
		show={show}
		hideFooter
		size={isMobileView ? 'sm' : undefined}
		maskClassName="actAsModalLoadingMask"
		zIndexBackdrop={platform === 'client' ? 'var(--bs-zindex-modal, 1055)' : undefined}
		onHideFunc={() => {}}
	>
		<ActAsSessionLoader label={label} name={name} t={t} />
	</BayRcDialog>
);

export default ActAsSessionOverlay;
